import React from 'react'
import styled, { keyframes } from 'styled-components'
import { line, radius, font, transition, cardStyle } from 'features/dashboard/tokens'
import WarningAmberRoundedIcon  from '@mui/icons-material/WarningAmberRounded'
import HomeWorkRoundedIcon      from '@mui/icons-material/HomeWorkRounded'
import InsightsRoundedIcon      from '@mui/icons-material/InsightsRounded'

const TABS = [
  { key: 'action',    label: 'To do today',  Icon: WarningAmberRoundedIcon, hint: 'What needs attention' },
  { key: 'buildings', label: 'Properties',   Icon: HomeWorkRoundedIcon,     hint: 'Per-property breakdown' },
  { key: 'trends',    label: 'Trends',       Icon: InsightsRoundedIcon,     hint: 'History & activity' },
]

/**
 * SAP-style horizontal tab strip that splits the dashboard body into
 * three focused views so the user never scrolls past one screen of
 * unrelated content. Header (KPIs / filter / altitude) stays persistent
 * above; only the body region changes per tab.
 *
 * Each tab also shows a small "count" badge so users see at a glance
 * which areas need attention without clicking through.
 */
const DashboardTabs = ({ active, onChange, counts = {} }) => (
  <Wrap role='tablist' aria-label='Dashboard view'>
    {TABS.map(({ key, label, Icon, hint }) => {
      const count = counts[key]
      const isActive = active === key
      return (
        <Tab
          key={key}
          $active={isActive}
          role='tab'
          aria-selected={isActive}
          onClick={() => onChange(key)}
          title={hint}
        >
          <TabIcon $active={isActive}><Icon style={{ fontSize: 18 }} /></TabIcon>
          <TabBody>
            <TabLabel>{label}</TabLabel>
            <TabHint $active={isActive}>{hint}</TabHint>
          </TabBody>
          {Number.isFinite(count) && count > 0 && (
            <TabBadge $active={isActive}>{count}</TabBadge>
          )}
          {isActive && <ActiveUnderline />}
        </Tab>
      )
    })}
  </Wrap>
)

export default DashboardTabs

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Wrap = styled.div`
  ${cardStyle}
  display: flex;
  gap: 4px;
  margin: 24px 0 16px;
  padding: 6px;
  overflow-x: auto;
`

const Tab = styled.button`
  position: relative;
  font: inherit;
  display: inline-flex; align-items: center; gap: 10px;
  flex: 1; min-width: 0;
  background: ${({ $active, theme }) => $active ? theme.colors.lighterGrey : 'transparent'};
  border: 1px solid ${({ $active }) => $active ? line.base : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.haiti : theme.colors.grey[800]};
  padding: 10px 16px;
  border-radius: ${radius.md};
  cursor: pointer;
  text-align: left;
  transition: ${transition};
  animation: ${slideIn} 240ms ease both;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.lighterGrey : 'rgba(51, 190, 204, 0.06)'};
    color: ${({ theme }) => theme.colors.haiti};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: -2px;
  }
`

const TabIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px;
  border-radius: ${radius.md};
  background: ${({ $active, theme }) => $active ? theme.colors.white : 'rgba(51, 190, 204, 0.10)'};
  border: 1px solid ${({ $active }) => $active ? '#33becc' : 'transparent'};
  color: ${({ $active }) => $active ? '#1f8c97' : '#33becc'};
  flex-shrink: 0;
`

const TabBody = styled.span`
  display: inline-flex; flex-direction: column; gap: 1px; min-width: 0;
`

const TabLabel = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
`

const TabHint = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.04em;
  color: ${({ $active, theme }) => $active ? theme.colors.grey[800] : theme.colors.grey[800]};
  white-space: nowrap;
`

const TabBadge = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 20px;
  padding: 0 7px;
  border-radius: ${radius.pill};
  font-size: ${font.caption};
  font-weight: 800;
  background: ${({ $active }) => $active ? '#b00020' : '#b0002015'};
  color: ${({ $active }) => $active ? '#fff' : '#b00020'};
  border: 1px solid ${({ $active }) => $active ? '#b00020' : '#b0002033'};
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`

const ActiveUnderline = styled.span`
  position: absolute;
  left: 14px; right: 14px; bottom: -2px;
  height: 2px;
  border-radius: ${radius.xs};
  background: ${({ theme }) => theme.colors.secondary};
`
